import { useMemo } from 'react';

/* =========================
   1️⃣ Quarterly EPS
========================= */
const useQuarterlyEps = (companyData) =>
  useMemo(() => {
    const qr = companyData?.data?.quaterly_results;
    if (!qr) return [];
    const headers = qr[0];
    const epsRow = qr.find(r => r[0] === 'EPS in Rs');
    if (!epsRow) return [];
    return headers
      .map((h, i) => {
        if (i === 0) return null;
        const eps = parseFloat(epsRow[i]);
        if (isNaN(eps)) return null;

        const date = new Date(`${h} 01`);
        if (isNaN(date)) return null;

        return { date, eps };
      })
      .filter(Boolean)
      .sort((a, b) => a.date - b.date);
  }, [companyData]);

/* =========================
   2️⃣ TTM EPS (Rolling 4Q)
========================= */
const useTtmEps = (quarterlyEps) =>
  useMemo(() => {
    if (quarterlyEps.length < 4) return [];

    const ttm = [];
    for (let i = 3; i < quarterlyEps.length; i++) {
      const value =
        quarterlyEps[i].eps +
        quarterlyEps[i - 1].eps +
        quarterlyEps[i - 2].eps +
        quarterlyEps[i - 3].eps;

      ttm.push({
        date: quarterlyEps[i].date,
        ttmEps: +value.toFixed(2)
      });
    }
    return ttm;
  }, [quarterlyEps]);

/* =========================
   3️⃣ Daily PE Series
========================= */
const useDailyPeSeries = (priceData, ttmEpsSeries) =>
  useMemo(() => {
    if (!priceData.length || !ttmEpsSeries.length) return [];

    let ttmIndex = 0;

    return priceData
      .map(p => {
        const date = new Date(p.date);
        const price = p.price;
        if (!price || isNaN(date)) return null;

        while (
          ttmIndex + 1 < ttmEpsSeries.length &&
          date >= ttmEpsSeries[ttmIndex + 1].date
        ) {
          ttmIndex++;
        }

        const ttmEps = ttmEpsSeries[ttmIndex]?.ttmEps;
        if (!ttmEps || ttmEps <= 0) return null;

        return {
          date: date.getTime(),
          pe: +(price / ttmEps).toFixed(2),
          ttmEps
        };
      })
      .filter(Boolean);
  }, [priceData, ttmEpsSeries]);

/* =========================
   4️⃣ Filter by Time Range
========================= */
const useFilteredPeSeries = (dailyPeSeries, timeRange) =>
  useMemo(() => {
    if (!dailyPeSeries.length || timeRange === 'Max') return dailyPeSeries;

    const cutoff = new Date();
    const map = {
      '1M': () => cutoff.setMonth(cutoff.getMonth() - 1),
      '6M': () => cutoff.setMonth(cutoff.getMonth() - 6),
      '1Yr': () => cutoff.setFullYear(cutoff.getFullYear() - 1),
      '3Yr': () => cutoff.setFullYear(cutoff.getFullYear() - 3),
      '5Yr': () => cutoff.setFullYear(cutoff.getFullYear() - 5),
      '10Yr': () => cutoff.setFullYear(cutoff.getFullYear() - 10)
    };

    map[timeRange]?.();
    return dailyPeSeries.filter(d => d.date >= cutoff.getTime());
  }, [dailyPeSeries, timeRange]);

/* =========================
   5️⃣ Median PE
========================= */
const useMedianPe = (data) =>
  useMemo(() => {
    const vals = data.map(d => d.pe).filter(v => v > 0).sort((a, b) => a - b);
    if (!vals.length) return null;
    const mid = Math.floor(vals.length / 2);
    return vals.length % 2 ? vals[mid] : (vals[mid - 1] + vals[mid]) / 2;
  }, [data]);

/* =========================
   6️⃣ Screener-style TTM Bars
   (ONLY when EPS changes)
========================= */
const buildTtmBars = (peSeries) => {
  const bars = [];
  let lastValue = null;

  peSeries.forEach(d => {
    if (d.ttmEps !== lastValue) {
      bars.push({
        date: d.date,
        ttmEps: d.ttmEps
      });
      lastValue = d.ttmEps;
    }
  });

  return bars;
};

/* =========================
   🔥 EXPORT MAIN HOOK
========================= */
export const usePeRatio = (companyData, priceData, timeRange) => {
  const quarterlyEps = useQuarterlyEps(companyData);
  const ttmEpsSeries = useTtmEps(quarterlyEps);
  const dailyPeSeries = useDailyPeSeries(priceData, ttmEpsSeries);
  const filteredPeSeries = useFilteredPeSeries(dailyPeSeries, timeRange);
  const medianPe = useMedianPe(filteredPeSeries);

  const ttmBars = useMemo(
    () => buildTtmBars(filteredPeSeries),
    [filteredPeSeries]
  );

  return {
    filteredPeSeries,
    ttmBars,
    medianPe
  };
};
